import React, { useState, useMemo } from 'react';
import styled from 'styled-components';
import { media } from '../../theme/tokens';
import { SelectDropdown, DataTable } from '../../components';
import { usePrimeursLive } from 'apiTyped/hooks/primeurs.query';
import type { SelectOption } from '../../components';

export const PrimeursLiveTab: React.FC = () => {
  const [vintage, setVintage] = useState<string>('');
  const [wine, setWine] = useState<string>('');
  const [market, setMarket] = useState<string>('');

  const { isLoading, live, client_wines } = usePrimeursLive(
    vintage ? { vintage } : undefined
  );

  const checks = live?.checks || [];
  const minimumPrices = live?.minimum_prices || [];

  // Build filter options from live data
  const wineOptions: SelectOption[] = useMemo(() => {
    const names = [...new Set(checks.map((c: any) => c.wine_name))];
    return [
      { value: '', label: 'All wines' },
      ...names.map((n) => ({ value: n as string, label: n as string }))
    ];
  }, [checks]);

  const vintageOptions: SelectOption[] = useMemo(() => {
    const vintages = Array.from(
      new Set(checks.map((c: any) => String(c.vintage)))
    )
      .sort()
      .reverse();
    return [
      { value: '', label: 'All vintages' },
      ...vintages.map((v: string) => ({ value: v, label: v }))
    ];
  }, [checks]);

  const marketOptions: SelectOption[] = useMemo(() => {
    const markets = [
      ...new Set(checks.map((c: any) => c.store_markets?.[0]).filter(Boolean))
    ];
    return [
      { value: '', label: 'All markets' },
      ...markets.map((m) => ({ value: m as string, label: m as string }))
    ];
  }, [checks]);

  // Filter data
  const filteredChecks = useMemo(() => {
    let data = [...checks];
    if (wine) data = data.filter((c: any) => c.wine_name === wine);
    if (market) data = data.filter((c: any) => c.store_markets?.[0] === market);
    return data;
  }, [checks, wine, market]);

  // Reference price for selected wine
  const referencePrice = useMemo(() => {
    if (!wine) return null;
    const ref = minimumPrices.find(
      (p: any) => p.wine_name === wine && p.currency_code === 'EUR'
    );
    if (!ref) return null;
    return { ...ref, min_price: (ref as any).value ?? (ref as any).min_price };
  }, [wine, minimumPrices]);

  const columns = useMemo(
    () => [
      {
        key: 'store_name',
        header: 'Store',
        render: (row: any) => row.store_name || '—',
        width: '20%'
      },
      {
        key: 'wine_name',
        header: 'Wine',
        render: (row: any) => row.wine_name,
        width: '25%'
      },
      {
        key: 'vintage',
        header: 'Vintage',
        render: (row: any) => row.vintage,
        width: '10%'
      },
      {
        key: 'market',
        header: 'Market',
        render: (row: any) => row.store_markets?.[0] || '—',
        width: '12%'
      },
      {
        key: 'price',
        header: 'Price (€ HT)',
        render: (row: any) => (
          <PriceCell
            $alert={
              referencePrice && row.ht_euro_price < referencePrice.min_price
            }
          >
            {row.ht_euro_price != null
              ? `${Number(row.ht_euro_price).toFixed(2)}€`
              : '—'}
          </PriceCell>
        ),
        width: '13%'
      },
      {
        key: 'deviation',
        header: 'Deviation',
        render: (row: any) => {
          if (row.pmm_deviation == null) return '—';
          const val = Number(row.pmm_deviation);
          return (
            <DeviationCell $negative={val < 0}>
              {val > 0 ? '+' : ''}
              {val.toFixed(1)}%
            </DeviationCell>
          );
        },
        width: '10%'
      },
      {
        key: 'date',
        header: 'Date',
        render: (row: any) => row.date || '—',
        width: '10%'
      }
    ],
    [referencePrice]
  );

  return (
    <Wrap>
      <FilterRow>
        <SelectDropdown
          options={wineOptions}
          value={wine}
          onChange={setWine}
          placeholder="Wine"
        />
        <SelectDropdown
          options={vintageOptions}
          value={vintage}
          onChange={setVintage}
          placeholder="Vintage"
        />
        <SelectDropdown
          options={marketOptions}
          value={market}
          onChange={setMarket}
          placeholder="Market"
        />
      </FilterRow>

      {referencePrice && (
        <ReferenceBar>
          <span>Min. price reference:</span>
          <strong>{Number(referencePrice.min_price).toFixed(2)}€</strong>
          <span>({referencePrice.wine_name})</span>
        </ReferenceBar>
      )}

      <DataTable
        columns={columns}
        data={filteredChecks}
        emptyMessage={
          isLoading ? 'Loading...' : 'No primeur live data available'
        }
      />
    </Wrap>
  );
};

// --- Styles ---

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const FilterRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  align-items: center;
`;

const ReferenceBar = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  background: var(--paper-warm);
  border-radius: var(--radius-md);
  font-size: 13px;
  color: var(--ink-muted);

  strong {
    color: var(--terracotta);
  }
`;

const PriceCell = styled.span<{ $alert?: boolean | null }>`
  font-weight: 600;
  color: ${({ $alert }) => ($alert ? 'var(--terracotta)' : 'var(--ink)')};
`;

const DeviationCell = styled.span<{ $negative: boolean }>`
  font-weight: 500;
  color: ${({ $negative }) =>
    $negative ? 'var(--terracotta)' : 'var(--olive)'};
`;
